import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, LogOut, ChevronDown, X, Mail, Phone, Menu } from 'lucide-react';
import { useAuthStore, type SocietyContact } from '../stores/auth-store';
import { authApi } from '../api/auth.api';
import { resolveLogoUrl } from '../features/societies/utils/logo.utils';
import { getEnvironmentConfig } from '@/config/domainConfigLoader';
import { GENERIC_CONTACT_PHONE } from '../constants/contacts';

const ContactBadge: React.FC<{ label: string; contact: SocietyContact }> = ({ label, contact }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="tw-relative tw-flex tw-items-center tw-gap-2.5 tw-cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {contact.avatar ? (
        <img
          src={contact.avatar}
          alt={`${contact.firstname} ${contact.lastname}`}
          className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover tw-flex-shrink-0"
        />
      ) : (
        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
          <User className="tw-w-5 tw-h-5 tw-text-gray-500" />
        </div>
      )}
      <div className="tw-flex tw-flex-col">
        <span className="tw-text-[10px] tw-uppercase tw-font-semibold tw-text-gray-400 tw-leading-none tw-mb-0.5">{label}</span>
        <span className="tw-text-sm tw-font-medium tw-text-gray-700 tw-leading-tight">
          {contact.firstname} {contact.lastname}
        </span>
      </div>

      {/* Popover au survol */}
      {isHovered && (
        <div className="tw-absolute tw-top-full tw-left-0 tw-mt-2 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-p-4 tw-z-50 tw-min-w-[280px]">
          <div className="tw-flex tw-items-center tw-gap-4">
            {contact.avatar ? (
              <img
                src={contact.avatar}
                alt={`${contact.firstname} ${contact.lastname}`}
                className="tw-w-16 tw-h-16 tw-rounded-full tw-object-cover tw-flex-shrink-0"
              />
            ) : (
              <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <User className="tw-w-8 tw-h-8 tw-text-gray-500" />
              </div>
            )}
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-semibold tw-text-gray-900">
                {contact.firstname} {contact.lastname}
              </span>
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-500 hover:tw-text-affinicar-primary">
                  <Mail className="tw-w-3.5 tw-h-3.5" />
                  {contact.email}
                </a>
              )}
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-500 hover:tw-text-affinicar-primary">
                  <Phone className="tw-w-3.5 tw-h-3.5" />
                  {contact.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PhoneContactBadge: React.FC<{ label: string; display: string; tel: string }> = ({ label, display, tel }) => (
  <a
    href={`tel:${tel}`}
    className="tw-flex tw-items-center tw-gap-2.5 tw-no-underline hover:tw-no-underline"
  >
    <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
      <Phone className="tw-w-5 tw-h-5 tw-text-gray-500" />
    </div>
    <div className="tw-flex tw-flex-col">
      <span className="tw-text-[10px] tw-uppercase tw-font-semibold tw-text-gray-400 tw-leading-none tw-mb-0.5">{label}</span>
      <span className="tw-text-sm tw-font-medium tw-text-gray-700 tw-leading-tight">{display}</span>
    </div>
  </a>
);

/**
 * Header horizontal fixé en haut
 * Affiche les informations utilisateur et actions compte
 */
interface HeaderProps {
  onMenuToggle?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const { currentUser, society, isImpersonating } = useAuthStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleLogout = () => {
    // La route /auth/logout invalide la session (API + locale) puis renvoie
    // vers /login.
    window.location.href = '/auth/logout';
  };

  const handleStopImpersonate = async () => {
    try {
      await authApi.stopImpersonate();

      const { refreshToken } = useAuthStore.getState();
      const adminUrl = getEnvironmentConfig().ADMIN_URL;

      if (adminUrl && refreshToken) {
        const base = adminUrl.startsWith('http') ? adminUrl : `https://${adminUrl}`;
        window.location.href = `${base}?authTransfer=${encodeURIComponent(refreshToken)}`;
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Erreur lors de l\'arrêt de l\'impersonation:', error);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Initiales de l'utilisateur pour l'avatar
  const userInitials = currentUser
    ? `${currentUser.firstname?.[0] || ''}${currentUser.lastname?.[0] || ''}`.toUpperCase()
    : 'U';

  // Logo de la société si disponible
  const societyLogoUrl = society ? resolveLogoUrl(society) : null;

  // Agence d'un groupe garant : seul le numéro de contact générique est affiché
  const isGroupGuaranteedAgency = !!society?.parentId && society.guarantorId === society.parentId;

  return (
    <header className="tw-fixed tw-top-0 tw-right-0 tw-left-0 tw-h-16 tw-bg-white tw-border-b tw-border-gray-200 tw-z-50">
      <div className="tw-h-full tw-flex tw-items-center">
        {/* Bouton hamburger mobile */}
        <button
          type="button"
          onClick={onMenuToggle}
          className="tw-flex md:tw-hidden tw-items-center tw-justify-center tw-w-12 tw-h-full tw-flex-shrink-0 tw-text-gray-600 hover:tw-text-gray-900 tw-transition-colors"
        >
          <Menu className="tw-w-6 tw-h-6" />
        </button>

        {/* Logo Affinicar — même largeur que la sidebar */}
        <div className="tw-hidden md:tw-flex tw-w-64 tw-h-full tw-flex-shrink-0 tw-items-center tw-justify-center tw-border-r tw-border-gray-200">
          <Link to="/dashboard?tab=dashboard">
            <img
              src="/assets/images/affinicar/logo.png"
              alt="Affinicar"
              className="tw-h-12 tw-w-auto"
            />
          </Link>
        </div>

        {/* Logo mobile (compact) */}
        <div className="tw-flex md:tw-hidden tw-h-full tw-items-center tw-px-2">
          <Link to="/dashboard?tab=dashboard">
            <img
              src="/assets/images/affinicar/logo.png"
              alt="Affinicar"
              className="tw-h-8 tw-w-auto"
            />
          </Link>
        </div>

        {/* Reste du header */}
        <div className="tw-flex-1 tw-px-3 sm:tw-px-4 md:tw-px-6 tw-flex tw-items-center tw-justify-between">
        {/* Partie gauche : Bouton mode preview */}
        <div className="tw-flex tw-items-center tw-gap-4">
          {isImpersonating && (
            <button
              onClick={handleStopImpersonate}
              className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-bg-gray-800 tw-text-white tw-text-xs tw-font-medium tw-rounded-md hover:tw-bg-gray-900 tw-transition-colors"
            >
              <X className="tw-w-3 tw-h-3" />
              <span>Sortir du mode preview de {currentUser?.firstname} {currentUser?.lastname}</span>
            </button>
          )}
        </div>

        {/* Centre : Contacts (EXTERNAL uniquement, masqués sur mobile).
            Agences d'un groupe garant : numéro générique ; sinon commercial / gestionnaire SAV */}
        {currentUser?.type === 'EXTERNAL' && isGroupGuaranteedAgency && (
          <div className="tw-hidden lg:tw-flex tw-items-center tw-gap-4">
            <PhoneContactBadge label="Contact" display={GENERIC_CONTACT_PHONE.display} tel={GENERIC_CONTACT_PHONE.tel} />
          </div>
        )}
        {currentUser?.type === 'EXTERNAL' && !isGroupGuaranteedAgency && (society?.commercial || society?.savManager) && (
          <div className="tw-hidden lg:tw-flex tw-items-center tw-gap-4">
            {society.commercial && (
              <ContactBadge label="Commercial" contact={society.commercial} />
            )}
            {society.commercial && society.savManager && (
              <div className="tw-w-px tw-h-8 tw-bg-gray-200" />
            )}
            {society.savManager && (
              <ContactBadge label="Gestionnaire SAV" contact={society.savManager} />
            )}
          </div>
        )}

        {/* Partie droite : Menu utilisateur */}
        <div className="tw-flex tw-items-center tw-gap-4">
          {/* Info utilisateur + dropdown */}
          <div className="tw-relative" ref={dropdownRef}>
            <button
              onClick={toggleDropdown}
              className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-rounded-lg tw-transition-colors hover:tw-bg-gray-50"
            >
              {/* Avatar - Logo société ou initiales utilisateur */}
              {societyLogoUrl ? (
                <div className="tw-w-8 tw-h-8 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
                  <img
                    src={societyLogoUrl}
                    alt={society?.name || 'Logo'}
                    className="tw-w-full tw-h-full tw-object-contain"
                  />
                </div>
              ) : (
                <div className="tw-w-8 tw-h-8 tw-bg-gradient-to-br tw-from-affinicar-primary tw-to-affinicar-secondary tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-text-sm tw-font-semibold">
                  {userInitials}
                </div>
              )}
              
              {/* Nom + rôle (masqués sur mobile) */}
              <div className="tw-hidden sm:tw-flex tw-flex-col tw-items-start">
                <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                  {currentUser?.firstname} {currentUser?.lastname}
                </span>
                <span className="tw-text-xs tw-text-gray-500">
                  {society?.name || 'Externe'}
                </span>
              </div>

              {/* Chevron */}
              <ChevronDown 
                className={`tw-w-4 tw-h-4 tw-text-gray-400 tw-transition-transform ${
                  isDropdownOpen ? 'tw-rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown menu */}
            {isDropdownOpen && (
              <div className="tw-absolute tw-right-0 tw-mt-2 tw-w-56 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-py-2">
                {/* Info utilisateur */}
                <div className="tw-px-4 tw-py-2 tw-border-b tw-border-gray-100">
                  <p className="tw-text-sm tw-text-gray-900 tw-mb-0.5">
                    {currentUser?.firstname} {currentUser?.lastname}
                  </p>
                  {currentUser?.type === 'EXTERNAL' && society && (
                    <p className="tw-text-xs tw-text-gray-500 tw-mb-0.5">{society.name}</p>
                  )}
                  <p className="tw-text-xs tw-text-gray-500">{currentUser?.email}</p>
                </div>

                {/* Actions */}
                <div className="tw-py-1">
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/profile');
                    }}
                    className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors"
                  >
                    <User className="tw-w-4 tw-h-4" />
                    <span>Mon profil</span>
                  </button>
                </div>

                {/* Déconnexion */}
                <div className="tw-border-t tw-border-gray-100 tw-py-1">
                  <button
                    onClick={handleLogout}
                    className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-transition-colors"
                  >
                    <LogOut className="tw-w-4 tw-h-4" />
                    <span>Déconnexion</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </header>
  );
};

